import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import useUserContext from 'Context/UserContext';
import { useMessagesContext } from 'Views/Page/Content/Templates/MessagesPage/MessagesContext';
import DataContainer from 'Components/UI/DataContainer';
import useMobileLayout from 'Hooks/useMobileLayout';
import HeaderBar from 'Components/ComponentLibrary/Molecules/HeaderBar';
import HamburgerMenuWithSiteNavigation from 'Components/ComponentLibrary/Molecules/HamburgerMenuWithSiteNavigation';
import { getLightBorder } from 'Components/utils';
import useBackgroundColor from 'Hooks/useBackgroundColor';
import { DataValueMap } from 'Helpers/Testing';
import Dock from 'Components/UI/Dock';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { useDockedContext } from 'Context/DockedContext';

import {
  ThreadSectionCollapseProvider,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ThreadsSection/ThreadCollapseContext';
import {
  MessagesPanel,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/MessagesPanel';
import {
  ThreadsSection,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ThreadsSection/ThreadsSection';
import ScenarioDetailsPicker
  from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ScenarioDetailsPicker';

const useStyles = buildUseStyles('MessagesPageContent', (theme) => ({
  root: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    '& > .FD-MuiContainer-root': {
      height: '100%',
    },
  },
  agentNav: {
    height: `calc(100vh - ${theme.heights.advisorNavigationHeight}px)`,
  },
  newNav: {
    height: `calc(100vh - ${theme.heights.desktopTopNavigationHeight}px)`,
  },
  mobileRootFallback: {
    height: `calc(100vh - ${theme.heights.mobileBottomNavigationHeight}px)`,
    width: '100vw',
  },
  mobileRoot: {
    height: `calc(100dvh - ${theme.heights.mobileBottomNavigationHeight}px)`,
    width: '100dvw',
  },
  content: {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    ...getLightBorder(theme, { top: true }),
  },
  desktopContent: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(2, 2),
    gap: theme.spacing(1),
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: theme.breakpoints.values.xl,
    margin: theme.spacing(0, 'auto'),
  },
  actionBar: {
    flexShrink: 0,
    padding: theme.spacing(0, 2),
  },
  actionBarMobile: {
    marginTop: theme.spacing(1),
  },
  actionBarDesktop: {
    marginTop: theme.spacing(1),
  },
}));

const MessagesPageContent = ({
  className,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMobileLayout();
  const backgroundColor = isMobile ? theme.palette.common.white : theme.palette.facet.lightGray;

  const newNav = true;

  useBackgroundColor(backgroundColor);
  const { isAgent } = useUserContext();
  const {
    isInitializing,
    initializingError,
  } = useMessagesContext();
  const { undockItem } = useDockedContext();

  useComponentDidMount(null, async () => {
    await undockItem('MessagesPageCompose');
  });

  const PageAppBar = () => {
    // App Bar in page context already exists for agents
    if (isAgent) {
      return null;
    }
    if (isMobile) {
      return (
        <HeaderBar
          title={'Messages'}
          left={<HamburgerMenuWithSiteNavigation color={'primaryGray'} />}
          TitleProps={{
            color: 'primaryGray',
          }}
        />
      );
    }
    return null;
  };

  return (
    <React.Fragment>
      <DataContainer
        loading={isInitializing}
        loadingError={initializingError}
      >
        <Box
          className={classNames(classes.root, className, {
            [classes.newNav]: newNav,
            [classes.mobileRootFallback]: isMobile,
            [classes.mobileRoot]: isMobile,
            [classes.agentNav]: isAgent,
          })}
          {...DataValueMap.init('messages-page-content').build()}
        >
          <PageAppBar />
          <Box className={classNames(classes.content, { [classes.desktopContent]: !isMobile })}>
            <ThreadSectionCollapseProvider initialState={false}>
              <ThreadsSection />
            </ThreadSectionCollapseProvider>
            <MessagesPanel />
            <ScenarioDetailsPicker />
          </Box>
        </Box>
      </DataContainer>
      <Dock />
    </React.Fragment>
  );
};

MessagesPageContent.defaultProps = {
  className: '',
};

MessagesPageContent.propTypes = {
  className: PropTypes.string,
};

export { MessagesPageContent };
